'use client'

import { ChevronLeft } from 'lucide-react'
import * as React from 'react'

import { Button, type ButtonProps } from '../button/Button'
import { cn } from '../lib/cn'

export interface BackHistoryButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** Called when there is browser history to go back to. Defaults to `window.history.back()`. */
  onBack?: () => void
  /** Called when no history is available (e.g. fresh tab). Use this to navigate to a default route. */
  onFallback?: () => void
}

const BackHistoryButton = React.forwardRef<HTMLButtonElement, BackHistoryButtonProps>(
  ({ onBack, onFallback, children, className, variant = 'link', ...props }, ref) => {
    const handleClick = React.useCallback(() => {
      if (typeof window !== 'undefined' && window.history.length > 2) {
        if (onBack) onBack()
        else window.history.back()
      } else {
        onFallback?.()
      }
    }, [onBack, onFallback])

    return (
      <Button
        ref={ref}
        variant={variant}
        className={cn('mr-4 p-4 pl-0', className)}
        onClick={handleClick}
        aria-label='Go back'
        {...props}
      >
        {children ?? <ChevronLeft />}
      </Button>
    )
  },
)
BackHistoryButton.displayName = 'BackHistoryButton'

export { BackHistoryButton }
